'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

// ============================================
// TIPOS
// ============================================

export type LogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug' | 'network' | 'event' | 'state' | 'sip';

export interface LogEntry {
  id: number;
  timestamp: Date;
  level: LogLevel;
  category: string;
  message: string;
  data?: unknown;
  stack?: string;
}

export interface DeviceInfo {
  userAgent: string;
  platform: string;
  language: string;
  cookieEnabled: boolean;
  onLine: boolean;
  screenWidth: number;
  screenHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  devicePixelRatio: number;
  touchSupport: boolean;
  mediaDevices: {
    audioInputs: number;
    audioOutputs: number;
    videoInputs: number;
  };
  permissions: {
    microphone?: string;
    notifications?: string;
  };
  connection?: {
    type?: string;
    effectiveType?: string;
    downlink?: number;
    rtt?: number;
  };
  memory?: {
    jsHeapSizeLimit?: number;
    totalJSHeapSize?: number;
    usedJSHeapSize?: number;
  };
}

export interface NetworkLog {
  id: number;
  timestamp: Date;
  method: string;
  url: string;
  status?: number;
  duration?: number;
  requestHeaders?: Record<string, string>;
  responseHeaders?: Record<string, string>;
  requestBody?: string;
  responseBody?: string;
  error?: string;
}

// ============================================
// GLOBAL LOG STORE
// ============================================

const MAX_LOGS = 1000;
const MAX_NETWORK_LOGS = 200;

let logIdCounter = 0;
let networkIdCounter = 0;

const globalLogs: LogEntry[] = [];
const networkLogs: NetworkLog[] = [];
const logListeners: Set<() => void> = new Set();

// Original console methods
const originalConsole = {
  log: console.log.bind(console),
  info: console.info.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console),
  debug: console.debug.bind(console),
};

// Flag para evitar re-interceptação
let isIntercepted = false;

/**
 * Adiciona um log ao store global
 */
export function addLog(
  level: LogLevel,
  category: string,
  message: string,
  data?: unknown,
  stack?: string
): LogEntry {
  const entry: LogEntry = {
    id: ++logIdCounter,
    timestamp: new Date(),
    level,
    category,
    message,
    data,
    stack,
  };

  globalLogs.push(entry);

  // Limitar tamanho
  while (globalLogs.length > MAX_LOGS) {
    globalLogs.shift();
  }

  // Notificar listeners
  logListeners.forEach((listener) => listener());

  return entry;
}

/**
 * Adiciona um log de rede
 */
export function addNetworkLog(log: Omit<NetworkLog, 'id' | 'timestamp'>): NetworkLog {
  const entry: NetworkLog = {
    id: ++networkIdCounter,
    timestamp: new Date(),
    ...log,
  };

  networkLogs.push(entry);

  while (networkLogs.length > MAX_NETWORK_LOGS) {
    networkLogs.shift();
  }

  logListeners.forEach((listener) => listener());

  return entry;
}

/**
 * Categoriza mensagem de log baseado no conteúdo
 */
function categorizeLog(args: unknown[]): { category: string; isSIP: boolean } {
  const firstArg = args[0];
  if (typeof firstArg === 'string') {
    // Callix Dialer logs
    if (firstArg.includes('[Callix Dialer]')) return { category: 'Callix', isSIP: false };
    if (firstArg.includes('[MobileDialer]')) return { category: 'MobileDialer', isSIP: false };
    if (firstArg.includes('[FloatingDialer]')) return { category: 'FloatingDialer', isSIP: false };
    if (firstArg.includes('[InlineDialer]')) return { category: 'InlineDialer', isSIP: false };
    if (firstArg.includes('[DraggableClientInfoModal]')) return { category: 'ClientInfo', isSIP: false };
    if (firstArg.includes('[WhatsApp')) return { category: 'WhatsApp', isSIP: false };
    if (firstArg.includes('[API')) return { category: 'API', isSIP: false };
    
    // SIP/WebRTC logs
    if (firstArg.includes('SIP') || firstArg.includes('sip:')) return { category: 'SIP', isSIP: true };
    if (firstArg.includes('WebSocket')) return { category: 'WebSocket', isSIP: true };
    if (firstArg.includes('WebRTC') || firstArg.includes('RTC')) return { category: 'WebRTC', isSIP: true };
    if (firstArg.includes('ICE')) return { category: 'ICE', isSIP: true };
    if (firstArg.includes('SRTP') || firstArg.includes('DTLS')) return { category: 'Media', isSIP: true };
    
    // SDK logs
    if (firstArg.includes('SDK') || firstArg.includes('sdk')) return { category: 'SDK', isSIP: false };
    if (firstArg.includes('CallOperator')) return { category: 'CallOperator', isSIP: false };
    
    // React/State logs
    if (firstArg.includes('useEffect') || firstArg.includes('useState')) return { category: 'React', isSIP: false };
    if (firstArg.includes('render') || firstArg.includes('Render')) return { category: 'React', isSIP: false };
  }
  
  return { category: 'General', isSIP: false };
}

/**
 * Formata argumentos do console para string
 */
function formatArgs(args: unknown[]): { message: string; data?: unknown } {
  if (args.length === 0) return { message: '' };
  
  const parts: string[] = [];
  let data: unknown = undefined;
  
  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    if (typeof arg === 'string') {
      parts.push(arg);
    } else if (typeof arg === 'number' || typeof arg === 'boolean') {
      parts.push(String(arg));
    } else if (arg instanceof Error) {
      parts.push(`${arg.name}: ${arg.message}`);
      data = { errorStack: arg.stack };
    } else if (arg !== undefined) {
      // Objeto ou array - guardar como data
      if (data === undefined) {
        data = arg;
      } else if (Array.isArray(data)) {
        (data as unknown[]).push(arg);
      } else {
        data = [data, arg];
      }
      parts.push(typeof arg === 'object' ? '[Object]' : String(arg));
    }
  }
  
  return { message: parts.join(' '), data };
}

/**
 * Intercepta console methods para capturar logs
 */
export function interceptConsole(): void {
  if (isIntercepted) return;
  isIntercepted = true;

  const createInterceptor = (level: LogLevel, original: typeof console.log) => {
    return (...args: unknown[]) => {
      // Chamar original primeiro
      original(...args);
      
      // Processar para nosso log
      const { category, isSIP } = categorizeLog(args);
      const { message, data } = formatArgs(args);
      
      // Capturar stack trace para erros
      let stack: string | undefined;
      if (level === 'error') {
        stack = new Error().stack?.split('\n').slice(2).join('\n');
      }
      
      addLog(isSIP ? 'sip' : level, category, message, data, stack);
    };
  };

  console.log = createInterceptor('log', originalConsole.log);
  console.info = createInterceptor('info', originalConsole.info);
  console.warn = createInterceptor('warn', originalConsole.warn);
  console.error = createInterceptor('error', originalConsole.error);
  console.debug = createInterceptor('debug', originalConsole.debug);
}

/**
 * Restaura console original
 */
export function restoreConsole(): void {
  if (!isIntercepted) return;
  isIntercepted = false;
  
  console.log = originalConsole.log;
  console.info = originalConsole.info;
  console.warn = originalConsole.warn;
  console.error = originalConsole.error;
  console.debug = originalConsole.debug;
}

/**
 * Intercepta fetch para logar requisições
 */
export function interceptFetch(): void {
  if (typeof window === 'undefined') return;
  
  const originalFetch = window.fetch;
  
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const startTime = Date.now();
    const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
    const method = init?.method || 'GET';
    
    // Log de request headers (sem auth tokens completos)
    const requestHeaders: Record<string, string> = {};
    if (init?.headers) {
      const headers = new Headers(init.headers);
      headers.forEach((value, key) => {
        if (key.toLowerCase().includes('auth') || key.toLowerCase().includes('token')) {
          requestHeaders[key] = value.substring(0, 20) + '...';
        } else {
          requestHeaders[key] = value;
        }
      });
    }
    
    // Request body (truncado)
    let requestBody: string | undefined;
    if (init?.body) {
      if (typeof init.body === 'string') {
        requestBody = init.body.substring(0, 500);
      } else if (init.body instanceof FormData) {
        requestBody = '[FormData]';
      } else {
        requestBody = '[Binary/Stream]';
      }
    }
    
    try {
      const response = await originalFetch(input, init);
      const duration = Date.now() - startTime;
      
      // Clone para ler body sem consumir
      const clonedResponse = response.clone();
      let responseBody: string | undefined;
      
      try {
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const json = await clonedResponse.json();
          responseBody = JSON.stringify(json).substring(0, 1000);
        } else if (contentType.includes('text')) {
          responseBody = (await clonedResponse.text()).substring(0, 500);
        } else {
          responseBody = `[${contentType || 'unknown content-type'}]`;
        }
      } catch {
        responseBody = '[Could not read response body]';
      }
      
      // Response headers
      const responseHeaders: Record<string, string> = {};
      response.headers.forEach((value, key) => {
        responseHeaders[key] = value;
      });
      
      addNetworkLog({
        method,
        url,
        status: response.status,
        duration,
        requestHeaders,
        responseHeaders,
        requestBody,
        responseBody,
      });
      
      // Log também no console
      const statusEmoji = response.ok ? '✅' : '❌';
      addLog('network', 'Network', `${statusEmoji} ${method} ${url} - ${response.status} (${duration}ms)`, {
        status: response.status,
        duration,
      });
      
      return response;
    } catch (error) {
      const duration = Date.now() - startTime;
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      addNetworkLog({
        method,
        url,
        duration,
        requestHeaders,
        requestBody,
        error: errorMessage,
      });
      
      addLog('network', 'Network', `❌ ${method} ${url} - FAILED: ${errorMessage}`, {
        error: errorMessage,
        duration,
      });
      
      throw error;
    }
  };
}

/**
 * Captura erros globais não tratados
 */
export function captureGlobalErrors(): () => void {
  if (typeof window === 'undefined') return () => {};
  
  const errorHandler = (event: ErrorEvent) => {
    addLog('error', 'GlobalError', `Uncaught: ${event.message}`, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    }, event.error?.stack);
  };
  
  const rejectionHandler = (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    const message = reason instanceof Error ? reason.message : String(reason);
    addLog('error', 'UnhandledRejection', `Unhandled Promise Rejection: ${message}`, {
      reason: reason instanceof Error ? { name: reason.name, message: reason.message } : reason,
    }, reason instanceof Error ? reason.stack : undefined);
  };
  
  window.addEventListener('error', errorHandler);
  window.addEventListener('unhandledrejection', rejectionHandler);
  
  return () => {
    window.removeEventListener('error', errorHandler);
    window.removeEventListener('unhandledrejection', rejectionHandler);
  };
}

/**
 * Coleta informações do dispositivo
 */
export async function collectDeviceInfo(): Promise<DeviceInfo> {
  const info: DeviceInfo = {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    cookieEnabled: navigator.cookieEnabled,
    onLine: navigator.onLine,
    screenWidth: screen.width,
    screenHeight: screen.height,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio,
    touchSupport: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
    mediaDevices: {
      audioInputs: 0,
      audioOutputs: 0,
      videoInputs: 0,
    },
    permissions: {},
  };
  
  // Media devices
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    info.mediaDevices = {
      audioInputs: devices.filter(d => d.kind === 'audioinput').length,
      audioOutputs: devices.filter(d => d.kind === 'audiooutput').length,
      videoInputs: devices.filter(d => d.kind === 'videoinput').length,
    };
  } catch (e) {
    addLog('warn', 'DeviceInfo', 'Failed to enumerate media devices', e);
  }
  
  // Permissions
  try {
    if ('permissions' in navigator) {
      const micPermission = await navigator.permissions.query({ name: 'microphone' as PermissionName });
      info.permissions.microphone = micPermission.state;
      
      const notifPermission = await navigator.permissions.query({ name: 'notifications' as PermissionName });
      info.permissions.notifications = notifPermission.state;
    }
  } catch (e) {
    // Algumas permissões podem não estar disponíveis
  }
  
  // Network info
  const conn = (navigator as unknown as { connection?: { type?: string; effectiveType?: string; downlink?: number; rtt?: number } }).connection;
  if (conn) {
    info.connection = {
      type: conn.type,
      effectiveType: conn.effectiveType,
      downlink: conn.downlink,
      rtt: conn.rtt,
    };
  }
  
  // Memory info (Chrome only)
  const perf = performance as unknown as { memory?: { jsHeapSizeLimit?: number; totalJSHeapSize?: number; usedJSHeapSize?: number } };
  if (perf.memory) {
    info.memory = {
      jsHeapSizeLimit: perf.memory.jsHeapSizeLimit,
      totalJSHeapSize: perf.memory.totalJSHeapSize,
      usedJSHeapSize: perf.memory.usedJSHeapSize,
    };
  }
  
  return info;
}

/**
 * Limpa todos os logs
 */
export function clearLogs(): void {
  globalLogs.length = 0;
  networkLogs.length = 0;
  logListeners.forEach((listener) => listener());
}

/**
 * Obtém todos os logs
 */
export function getLogs(): LogEntry[] {
  return [...globalLogs];
}

/**
 * Obtém logs de rede
 */
export function getNetworkLogs(): NetworkLog[] {
  return [...networkLogs];
}

/**
 * Exporta logs para texto formatado
 */
export async function exportLogsAsText(): Promise<string> {
  const deviceInfo = await collectDeviceInfo();
  const lines: string[] = [];
  
  lines.push('═══════════════════════════════════════════════════════════════');
  lines.push('                    CALLIX DIALER DEBUG LOG                     ');
  lines.push('═══════════════════════════════════════════════════════════════');
  lines.push(`Exported: ${new Date().toISOString()}`);
  lines.push('');
  
  // Device Info
  lines.push('┌─────────────────────────────────────────────────────────────┐');
  lines.push('│                      DEVICE INFO                            │');
  lines.push('├─────────────────────────────────────────────────────────────┤');
  lines.push(`│ User Agent: ${deviceInfo.userAgent.substring(0, 50)}...`);
  lines.push(`│ Platform: ${deviceInfo.platform}`);
  lines.push(`│ Language: ${deviceInfo.language}`);
  lines.push(`│ Online: ${deviceInfo.onLine}`);
  lines.push(`│ Screen: ${deviceInfo.screenWidth}x${deviceInfo.screenHeight}`);
  lines.push(`│ Viewport: ${deviceInfo.viewportWidth}x${deviceInfo.viewportHeight}`);
  lines.push(`│ Pixel Ratio: ${deviceInfo.devicePixelRatio}`);
  lines.push(`│ Touch Support: ${deviceInfo.touchSupport}`);
  lines.push(`│ Audio Inputs: ${deviceInfo.mediaDevices.audioInputs}`);
  lines.push(`│ Audio Outputs: ${deviceInfo.mediaDevices.audioOutputs}`);
  lines.push(`│ Microphone Permission: ${deviceInfo.permissions.microphone || 'unknown'}`);
  if (deviceInfo.connection) {
    lines.push(`│ Connection Type: ${deviceInfo.connection.effectiveType || deviceInfo.connection.type || 'unknown'}`);
    lines.push(`│ RTT: ${deviceInfo.connection.rtt ?? 'unknown'}ms`);
  }
  if (deviceInfo.memory) {
    const usedMB = Math.round((deviceInfo.memory.usedJSHeapSize || 0) / 1024 / 1024);
    const totalMB = Math.round((deviceInfo.memory.totalJSHeapSize || 0) / 1024 / 1024);
    lines.push(`│ Memory: ${usedMB}MB / ${totalMB}MB`);
  }
  lines.push('└─────────────────────────────────────────────────────────────┘');
  lines.push('');
  
  // Network Logs
  lines.push('┌─────────────────────────────────────────────────────────────┐');
  lines.push('│                     NETWORK REQUESTS                        │');
  lines.push('├─────────────────────────────────────────────────────────────┤');
  if (networkLogs.length === 0) {
    lines.push('│ No network requests logged');
  } else {
    for (const log of networkLogs) {
      const time = log.timestamp.toISOString().split('T')[1].split('.')[0];
      const status = log.status ? `${log.status}` : 'FAIL';
      const duration = log.duration ? `${log.duration}ms` : '-';
      lines.push(`│ [${time}] ${log.method} ${status} (${duration})`);
      lines.push(`│   URL: ${log.url.substring(0, 60)}${log.url.length > 60 ? '...' : ''}`);
      if (log.error) {
        lines.push(`│   ERROR: ${log.error}`);
      }
    }
  }
  lines.push('└─────────────────────────────────────────────────────────────┘');
  lines.push('');
  
  // Application Logs
  lines.push('┌─────────────────────────────────────────────────────────────┐');
  lines.push('│                    APPLICATION LOGS                         │');
  lines.push('├─────────────────────────────────────────────────────────────┤');
  
  const levelIcons: Record<LogLevel, string> = {
    log: '📝',
    info: 'ℹ️',
    warn: '⚠️',
    error: '❌',
    debug: '🔍',
    network: '🌐',
    event: '⚡',
    state: '📊',
    sip: '📞',
  };
  
  for (const log of globalLogs) {
    const time = log.timestamp.toISOString().split('T')[1].split('.')[0];
    const icon = levelIcons[log.level] || '•';
    const level = log.level.toUpperCase().padEnd(7);
    const category = `[${log.category}]`.padEnd(20);
    
    lines.push(`│ ${time} ${icon} ${level} ${category}`);
    lines.push(`│   ${log.message.substring(0, 70)}${log.message.length > 70 ? '...' : ''}`);
    
    if (log.data) {
      try {
        const dataStr = JSON.stringify(log.data, null, 2);
        const dataLines = dataStr.split('\n');
        for (const dataLine of dataLines.slice(0, 10)) {
          lines.push(`│     ${dataLine}`);
        }
        if (dataLines.length > 10) {
          lines.push(`│     ... (${dataLines.length - 10} more lines)`);
        }
      } catch {
        lines.push(`│     [Data could not be serialized]`);
      }
    }
    
    if (log.stack) {
      const stackLines = log.stack.split('\n').slice(0, 5);
      for (const stackLine of stackLines) {
        lines.push(`│     ${stackLine.trim()}`);
      }
    }
    
    lines.push('│');
  }
  
  lines.push('└─────────────────────────────────────────────────────────────┘');
  lines.push('');
  lines.push(`Total Logs: ${globalLogs.length}`);
  lines.push(`Total Network Requests: ${networkLogs.length}`);
  lines.push('═══════════════════════════════════════════════════════════════');
  
  return lines.join('\n');
}

// ============================================
// HOOK
// ============================================

export function useDebugLogger() {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [network, setNetwork] = useState<NetworkLog[]>([]);
  const [updateTrigger, setUpdateTrigger] = useState(0);
  const isInitialized = useRef(false);
  
  // Inicializa interceptors uma vez
  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;
    
    interceptConsole();
    interceptFetch();
    const cleanupErrors = captureGlobalErrors();
    
    // Log inicial
    addLog('info', 'DebugLogger', '🚀 Debug Logger initialized', {
      timestamp: new Date().toISOString(),
      version: '1.0.0',
    });
    
    return () => {
      cleanupErrors();
      // Não restaurar console para manter logs entre re-renders
    };
  }, []);
  
  // Listener para atualizações
  useEffect(() => {
    const listener = () => {
      setUpdateTrigger((t) => t + 1);
    };
    
    logListeners.add(listener);
    return () => {
      logListeners.delete(listener);
    };
  }, []);
  
  // Atualiza estado quando logs mudam
  useEffect(() => {
    setLogs(getLogs());
    setNetwork(getNetworkLogs());
  }, [updateTrigger]);
  
  const clear = useCallback(() => {
    clearLogs();
  }, []);
  
  const exportText = useCallback(async () => {
    return exportLogsAsText();
  }, []);
  
  const copyToClipboard = useCallback(async () => {
    const text = await exportLogsAsText();
    try {
      await navigator.clipboard.writeText(text);
      addLog('info', 'DebugLogger', '📋 Logs copied to clipboard');
      return true;
    } catch (error) {
      addLog('error', 'DebugLogger', 'Failed to copy logs to clipboard', error);
      return false;
    }
  }, []);
  
  const addCustomLog = useCallback((level: LogLevel, category: string, message: string, data?: unknown) => {
    addLog(level, category, message, data);
  }, []);
  
  return {
    logs,
    networkLogs: network,
    clear,
    exportText,
    copyToClipboard,
    addLog: addCustomLog,
    collectDeviceInfo,
  };
}

// ============================================
// CÓDIGO SECRETO
// ============================================

const DEBUG_SECRET_CODE = '*#DEBUG#';
const DEBUG_SECRET_CODE_ALT = '*#0000#';

export function checkDebugCode(dialValue: string): boolean {
  const normalized = dialValue.toUpperCase().replace(/\s/g, '');
  return normalized === DEBUG_SECRET_CODE || normalized === DEBUG_SECRET_CODE_ALT;
}
